import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { askForAccess, listOwnRequests, withdrawRequest, type AccessRequest } from "../../api/access/accessRequestsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../buttons/AppButton";
import Field from "../forms/Field";
import { FORM_CONTROL_CLASS } from "../styles/formStyles";
import LoadStatus from "./LoadStatus";

/**
 * Ask for a missing permission, with an optional reason (accounts.md GS-8, redesign R3-5); once
 * one's own request waits, when it was asked, and Withdraw. Whoever could grant it sees it in
 * their list of requests.
 */
export default function AskForAccess({ projectId, needs }: { projectId: string | null; needs: string }) {
  const { t, i18n } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listOwnRequests(signal), []);
  const { state: own, reload } = useLoad(load);
  const [asked, setAsked] = useState<AccessRequest | null | undefined>(undefined);
  const [reason, setReason] = useState("");
  const { state, submit } = useSubmission();

  if (own.status !== "loaded") return <LoadStatus state={own} loadingLabel={t("common.loading")} onRetry={reload} compact={true} />;
  const waiting = asked !== undefined ? asked : (own.value.find((request) => request.state === "pending" && request.projectId === projectId && request.needs === needs) ?? null);
  const pending = state.status === "submitting";

  if (waiting !== null) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p>{t("refusals.blocked.requested", { date: formatDateTime(waiting.requestedAt, i18n.language) })}</p>
        <AppButton variant="secondary" size="sm" disabled={pending}
          onClick={() => { submit(async () => { await withdrawRequest(waiting.requestId); setAsked(null); }); }}>
          {t("refusals.blocked.withdraw")}
        </AppButton>
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </div>
    );
  }
  return (
    <form className="space-y-2"
      onSubmit={(event) => { event.preventDefault(); submit(async () => { setAsked(await askForAccess(projectId, needs, reason.trim())); setReason(""); }); }}>
      <Field label={t("refusals.blocked.reason")}>
        {(control) => <input {...control} className={FORM_CONTROL_CLASS} value={reason} maxLength={500} disabled={pending} onChange={(event) => { setReason(event.target.value); }} />}
      </Field>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      <div className="flex justify-end">
        <AppButton type="submit" variant="primary" size="sm" disabled={pending}>{t("refusals.blocked.ask")}</AppButton>
      </div>
    </form>
  );
}
